/**
 * Modal shown after a successful taxonomy edit — per `feature-spec.md`
 * §3.6.3. Driven by the response from B12-BE's `POST /impact` endpoint.
 *
 * Composition mirrors the B10/B11 precedents (`AgreementModal`,
 * `RecordResolvedModal`): controlled `open` + `onOpenChange`, two CTAs
 * in the `DialogFooter`. Outside-click / X dismissal is treated as
 * "Continue editing" by the parent.
 */
import React from 'react';

import { Badge } from '../../../../../ui/badge';
import { Button } from '../../../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../ui/dialog';
import type { ImpactResult } from '../../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  summary: ImpactResult;
  onContinueEditing: () => void;
  onGoToQueue: () => void;
}

const ReannotationImpactModal: React.FC<Props> = ({
  open,
  onOpenChange,
  summary,
  onContinueEditing,
  onGoToQueue,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="reannotation-impact-modal">
      <DialogHeader>
        <DialogTitle>Re-annotation impact</DialogTitle>
        <DialogDescription>
          Records annotated with the previous taxonomy version may not
          include the new label and should be re-annotated.
        </DialogDescription>
      </DialogHeader>

      {/* What changed */}
      <div className="space-y-2 py-2" data-testid="impact-changed-section">
        <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          What changed
        </div>
        {summary.changedDimensions.length > 0 ? (
          <div className="flex flex-wrap gap-1.5" data-testid="impact-changed-dimensions">
            {summary.changedDimensions.map((cd, idx) => (
              <Badge
                key={`${cd.dimensionId}-${idx}`}
                variant="default"
                data-testid={`impact-change-${idx}`}
              >
                {cd.dimensionName} ({cd.changeType})
              </Badge>
            ))}
          </div>
        ) : (
          <p
            className="text-[12px] italic text-muted-foreground"
            data-testid="impact-changed-empty"
          >
            Label or category changes detected (no dimension added or removed).
          </p>
        )}
      </div>

      {/* Stat strip */}
      <dl
        className="grid grid-cols-3 gap-3 rounded-lg border border-border bg-muted/30 p-3"
        data-testid="impact-stat-strip"
      >
        <div className="flex flex-col gap-0.5" data-testid="impact-stat-records-affected">
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Records affected
          </dt>
          <dd className="font-jakarta text-2xl font-extrabold tracking-[-0.02em] text-foreground">
            {summary.recordsAffected}
          </dd>
        </div>
        <div className="flex flex-col gap-0.5" data-testid="impact-stat-finalised">
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Finalised to review
          </dt>
          <dd className="font-jakarta text-2xl font-extrabold tracking-[-0.02em] text-foreground">
            {summary.finalisedRecordsToReview}
          </dd>
        </div>
        <div className="flex flex-col gap-0.5" data-testid="impact-stat-pending">
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Pending records
          </dt>
          <dd className="font-jakarta text-2xl font-extrabold tracking-[-0.02em] text-foreground">
            {summary.pendingRecords}
          </dd>
        </div>
      </dl>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onContinueEditing}
          data-testid="impact-continue-editing"
        >
          Continue editing
        </Button>
        <Button
          type="button"
          onClick={onGoToQueue}
          data-testid="impact-go-to-queue"
        >
          Go to Annotation Queue ({summary.recordsAffected} records)
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default ReannotationImpactModal;
